import { type ColorFrom, Feature } from '@tencent/workbench';
import { ISolidFillFeature } from '@tencent/drawing-drawer-components';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { STSchemeColorVal } from '@tencent/slide-sdk-pb-model';
import { NumberInputFrom } from '@tencent/wb-ext-form';
import {
    handleInputBlurInitAndReset,
    IInputParams,
} from '@src/slide/application/service/drawer-input-manager/drawer-handler';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import { SolidFill as SolidFillColor } from '@src/slide/domain/ui-model/plugin/referModels/FillClass';
import { BaseFillFeature } from '@framework/workbench/drawer/ui/fill/base/base-fill-feature';
import { TDWVer5Type, TDWVer6Type } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { FillType } from '@framework/workbench/drawer/ui/fill/constants';
import {
    ISolidFill,
} from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { SLIDE_ADAPTER } from '@framework/workbench/ssr/slide-adapter';

interface ISolidValue {
    solidFillColor: TDColor;
}

@Feature({
    name: 'SolidFillFeature',
    adapters: [SLIDE_ADAPTER],
    provide: ISolidFillFeature,
})
export class SolidFillFeature extends BaseFillFeature implements ISolidFillFeature {
    public value: ISolidValue;
    public constructor() {
        super();
        this.initValue();
        this.addInitPropsCallback(this.getInputParams().key);
    }

    public getInputParams(): IInputParams {
        return {
            key: INPUT_PROPS_CALLBACK_KEY.SOLID_FILL,
        };
    }

    public initPropsCallback = () => {
        if (this.fillType !== FillType.SOLID_FILL) {
            return;
        }
        this.value.solidFillColor = (this.fillInfo as ISolidFill)?.color
            || this.value.solidFillColor
            || new TDColor({ themeKey: STSchemeColorVal.accent1 });
    };

    public getSolidProps = () => this.value.solidFillColor;

    public handleSolidFillColorChange = (color: string, from?) => {
        this.getColorPickerManager()?.setRecentColor(color);
        this.handleColorChange(new TDColor({ rgbaStr: color }), from);
    };

    @handleInputBlurInitAndReset()
    public handleSolidFillOpacityChange(value: number, dragEnd?: boolean, from?: NumberInputFrom) {
        const { solidOpacity } = this.getValidValue({ solidOpacity: value }, from);
        this.value.solidFillColor.updateOpacity(solidOpacity);
        const onlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        this.confirmSolidChoice(this.value.solidFillColor, onlyChangeUI);
        this.tdwDrawerReportNumberInput(FormItemName.SOLID_FILL_OPACITY, { ver6: solidOpacity }, from, onlyChangeUI);
    }

    public getSolidFillColor = () => this.getFillValue();

    public handleColorChange = (tdColor: TDColor, from) => {
        const value = this.getSolidFillColor();
        this.handleSolidColorChange(
            tdColor,
            from,
            false,
        );
        this.getColorPickerManager()?.colorPickerTdwReport({
            color: value?.srgb_color || '',
            colorPickerFrom: TDWVer6Type.COLOR_PICKER_DRAWER,
            colorGroupFrom: from,
            actionType: TDWVer5Type.COLOR_PICKER_FILL,
        });
    };

    public confirmSolidChoice = (tdColor: TDColor | null, onlyChangeUI?: boolean) => {
        if (tdColor) {
            const solidFill = new SolidFillColor();
            solidFill.color = tdColor;
            this.updateFillProps({ solidFill }, onlyChangeUI);
        } else { // 无填充
            this.updateFillProps({ noFill: 1 }, onlyChangeUI);
        }
    };

    private handleSolidColorChange = (tdColor: TDColor, from: ColorFrom | number, onlyChangeUI) => {
        this.value.solidFillColor = tdColor;
        this.confirmSolidChoice(this.value.solidFillColor, onlyChangeUI);
    };

    private initValue() {
        const params: any = {};
        if (this.isSlideBg) {
            params.themeKey = STSchemeColorVal.bg1;
        } else {
            params.themeKey = STSchemeColorVal.accent1;
        }
        this.value = {
            solidFillColor: new TDColor(params),
        };
    }
}
